import { useContext } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserContext } from "@/components/providers/UserContext/UserContext";
import { loginUser, registerUser, logoutUser } from "@/services/user-services";

export function useAuth() {
  const { setUser, setViewAsId } = useContext(UserContext);
  const queryClient = useQueryClient();

  const loginMutation = useMutation({
    mutationFn: loginUser,
    onSuccess: (response) => {
      // Response is unwrapped by axios interceptor: { user, csrfToken }
      if (response?.user) {
        const { user } = response;
        // Defensive: guarantee a clean slate even if the previous session
        // ended without an explicit logout (e.g. token expiry, tab closed).
        queryClient.clear();
        localStorage.removeItem("viewAsUserId");
        setViewAsId?.(null);
        // Cookies are set automatically by the server
        localStorage.setItem("user", JSON.stringify(user));
        setUser(user);
      }
    },
  });

  const registerMutation = useMutation({
    mutationFn: registerUser,
  });

  const logoutMutation = useMutation({
    mutationFn: logoutUser,
    onSuccess: () => {
      // Clear user data
      localStorage.removeItem("user");
      setUser(null);
      // Clear impersonation state so no "View As" banner leaks into the next session
      localStorage.removeItem("viewAsUserId");
      setViewAsId?.(null);
      // Drop all cached server data so the next user doesn't see stale data
      queryClient.clear();
    },
  });

  return {
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    loginResult: {
      isLoading: loginMutation.isPending,
      error: loginMutation.error,
      isSuccess: loginMutation.isSuccess,
      data: loginMutation.data,
    },
    registerResult: {
      isLoading: registerMutation.isPending,
      error: registerMutation.error,
      isSuccess: registerMutation.isSuccess,
      data: registerMutation.data,
    },
    logoutResult: {
      isLoading: logoutMutation.isPending,
      error: logoutMutation.error,
      isSuccess: logoutMutation.isSuccess,
    },
  };
}
